import { RichContent } from '@corratech/pylot-rich-content'
import React from 'react'
import { GridPage } from './GridPage'
import chunk from 'lodash/chunk'
import s from './Plpgrid.module.scss'
import cn from 'classnames'

type MarketingTile = {
    columnSpan: {
        col: number
        span: number
    }
    content: string
    class: string
    position: number
}

type PlpGridPropTypes<P> = {
    products: P[]
    ProductTile: React.ComponentType<P>
    LoadingTile: React.ComponentType
    marketingTiles?: MarketingTile[]
    pageSize?: number
    isLoading?: boolean
    hideMarketingBlock?: boolean
    showMarketingBlockOnLessProducts?: boolean
    plpColumns?: number
}

export const PLPGrid = <P,>({
    products,
    ProductTile,
    LoadingTile,
    plpColumns = 4,
    marketingTiles = [],
    pageSize,
    isLoading = false,
    hideMarketingBlock = false,
    showMarketingBlockOnLessProducts = false
}: PlpGridPropTypes<P>): JSX.Element => {
    const processedMarketing = marketingTiles.map((item) => ({
        ...item,
        startIdx:
            showMarketingBlockOnLessProducts && products.length < item.position
                ? products.length - 1
                : item.position - 1
    }))

    // Map de clases de columnas por breakpoint (tailwind/utility-classes)
    const gridColumnClasses = [
        '',
        'sm:grid-cols-1 grid-cols-2',
        'sm:grid-cols-2 grid-cols-2',
        'sm:grid-cols-3 grid-cols-2',
        'sm:grid-cols-4 grid-cols-2',
        'sm:grid-cols-5 grid-cols-2',
        'sm:grid-cols-6 grid-cols-2',
        // 7 cols solo en xl+; en viewports menores cae a 4 cols (sm+) o 2 (mobile)
        'grid-cols-2 sm:grid-cols-4 xl:grid-cols-7'
    ]
    const safeCols =
        typeof plpColumns === 'number'
            ? Math.min(Math.max(plpColumns, 1), 7)
            : 4

    const getMarketing = (idx: number) => {
        const filteredMarketing = processedMarketing.filter(
            (item) => item.startIdx === idx
        )
        if (filteredMarketing.length) {
            const m = filteredMarketing[0]
            if (!m.content || !m.content.trim()) return null
            return (
                <div
                    className={cn(m.class, 'marketing-tile')}
                    style={{
                        gridColumn: `${m.columnSpan.col} / span ${m.columnSpan.span}`
                    }}
                >
                    <RichContent html={m.content} />
                </div>
            )
        }
        return null
    }

    if (isLoading && products.length === 0) {
        return (
            <div
                className={cn(s.Plpgrid, gridColumnClasses[safeCols])}
                data-cols={safeCols}
            >
                <GridPage
                    startingIndex={0}
                    items={[...Array(pageSize).keys()]}
                    ProductTile={LoadingTile}
                    getMarketing={getMarketing}
                />
            </div>
        )
    }

    return (
        <div
            className={cn(s.Plpgrid, gridColumnClasses[safeCols])}
            data-cols={safeCols}
        >
            {chunk(products, pageSize ?? products.length).map((items, key) => (
                <GridPage
                    key={key}
                    startingIndex={pageSize ? key * pageSize : 0}
                    items={items}
                    ProductTile={ProductTile}
                    getMarketing={getMarketing}
                    hideMarketingBlock={hideMarketingBlock}
                />
            ))}
        </div>
    )
}
